/**
 * Guides Slice
 *
 * Manages user guide/tooltip dismissal state.
 * Tracks which guides have been dismissed by the user to avoid showing them again.
 *
 * Persisted to localStorage via Zustand middleware in store/index.ts
 *
 * @module store/slices/guidesSlice
 */

// ===== SLICE INTERFACE =====

export interface GuidesSlice {
  guides: {
    /** Array of dismissed guide IDs (persisted to localStorage) */
    dismissedGuides: string[];

    /** Array of session-dismissed guide IDs (NOT persisted, resets on refresh) */
    sessionDismissedGuides: string[];

    /** Dismiss a single guide by ID */
    dismissGuide: (guideId: string) => void;

    /** Dismiss multiple guides at once (for carousel "don't show again") */
    dismissGuides: (guideIds: string[]) => void;

    /** Dismiss multiple guides for current session only (NOT persisted) */
    dismissGuidesForSession: (guideIds: string[]) => void;

    /** Clear all session-level dismissals (for testing/debugging) */
    clearSessionDismissals: () => void;

    /** Check if a guide has been dismissed */
    isGuideDismissed: (guideId: string) => boolean;

    /** Reset all dismissed guides (for testing/debugging) */
    resetDismissedGuides: () => void;
  };
}

// ===== SLICE CREATOR =====

export const createGuidesSlice = (
  set: (fn: (state: GuidesSlice) => void) => void,
  get: () => GuidesSlice,
  _store: unknown
) => ({
  guides: {
    dismissedGuides: [],
    sessionDismissedGuides: [],

    dismissGuide: (guideId: string) => {
      set((state) => {
        if (!state.guides.dismissedGuides.includes(guideId)) {
          state.guides.dismissedGuides.push(guideId);
        }
      });
    },

    dismissGuides: (guideIds: string[]) => {
      set((state) => {
        guideIds.forEach((id) => {
          if (!state.guides.dismissedGuides.includes(id)) {
            state.guides.dismissedGuides.push(id);
          }
        });
      });
    },

    dismissGuidesForSession: (guideIds: string[]) => {
      set((state) => {
        guideIds.forEach((id) => {
          if (!state.guides.sessionDismissedGuides.includes(id)) {
            state.guides.sessionDismissedGuides.push(id);
          }
        });
      });
    },

    clearSessionDismissals: () => {
      set((state) => {
        state.guides.sessionDismissedGuides = [];
      });
    },

    isGuideDismissed: (guideId: string) => {
      const state = get();
      return state.guides.dismissedGuides.includes(guideId);
    },

    resetDismissedGuides: () => {
      set((state) => {
        state.guides.dismissedGuides = [];
      });
    },
  },
});
